import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Leaf, Anchor, Download } from "lucide-react";
import { SITE, IMAGES, STATS } from "../data/site";
import { PRODUCTS, CATEGORIES } from "../data/products";
import { MaskedLine, Reveal, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { Counter } from "../components/Counter";

const ATTRIBUTES = [
  { icon: ShieldCheck, title: "Quality", text: "Every batch ships with an independent certificate of analysis — SGS or Bureau Veritas witnessed on request." },
  { icon: Leaf, title: "Sustainability", text: "Low-PCA, REACH-conscious grades and optimized logistics that cut unnecessary mileage from every supply chain." },
  { icon: Anchor, title: "Reliability", text: "On-spec, on-time delivery ex-Jebel Ali — freight, insurance and documentation handled by a single accountable desk." },
];

const DOCTRINE = [
  { n: "01", title: "Precision Sourcing", text: "We buy directly from tier-one refineries in the Gulf and Asia, locking grades to your spec sheet before a single ton moves." },
  { n: "02", title: "Chemical Purity Guarantee", text: "No cargo leaves without a CoA. Off-spec means rejected — a rule that has cost us shipments and kept us customers." },
  { n: "03", title: "Intercontinental Logistics", text: "Flexi-tanks, ISO tanks, drums or bulk vessel — our desk arranges freight, insurance and documentation door to port." },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 700], [0, 190]);
  const opacityBg = useTransform(scrollY, [0, 600], [1, 0.35]);

  return (
    <section data-testid="hero-section" className="relative isolate flex min-h-screen items-center overflow-hidden noise-surface">
      <motion.div style={{ y: yBg, opacity: opacityBg }} className="absolute inset-0 -z-10">
        <img src={IMAGES.hero} alt="Oil refinery at dusk" className="h-[115%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A101D] via-[#0A101D]/80 to-[#0A101D]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A101D] via-transparent to-[#0A101D]/60" />
      </motion.div>

      <div className="mx-auto w-full max-w-7xl px-5 pt-32 pb-20 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-amber-500"
          data-testid="hero-eyebrow"
        >
          Petroleum Trading — Dubai, UAE
        </motion.p>
        <h1 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.25}>Premium Petroleum</MaskedLine>
          <MaskedLine delay={0.4}>Solutions for</MaskedLine>
          <MaskedLine delay={0.55} innerClassName="text-amber-500">Critical Industries</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-slate-300"
          data-testid="hero-subcopy"
        >
          Rubber process oils, base oils, waxes, lubricants and steel products — sourced from tier-one
          refineries and mills, verified batch by batch, and shipped worldwide from the heart of Dubai.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/products"
            data-testid="hero-explore-products-button"
            className="group inline-flex items-center gap-2 bg-amber-500 px-7 py-3.5 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
          >
            Explore Products
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-request-quote-button"
            className="inline-flex items-center gap-2 border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-amber-500/70 hover:text-amber-400"
          >
            Request a Quote
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-6"
          data-testid="hero-trust-strip"
        >
          {["RPO", "BASE OILS", "PARAFFIN WAX", "LUBRICANTS", "SLACK WAX", "RESIDUE WAX", "PBR", "SBR", "SPH", "STEEL"].map((t) => (
            <span key={t} className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{t}</span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const AttributesStrip = () => (
  <section data-testid="attributes-strip" className="border-b border-black/10 bg-[#F5F5F2]">
    <div className="mx-auto grid max-w-7xl md:grid-cols-3">
      {ATTRIBUTES.map((a, i) => (
        <Reveal key={a.title} delay={i * 0.12} className={`px-8 py-12 ${i > 0 ? "border-t md:border-t-0 md:border-l border-black/10" : ""}`}>
          <a.icon className="h-6 w-6 text-amber-500" />
          <h3 className="mt-5 font-display text-2xl font-semibold uppercase tracking-wide text-[#0E1526]">{a.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{a.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const AboutSnapshot = () => (
  <section data-testid="about-snapshot" className="bg-white py-24 md:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
      <Reveal className="relative">
        <div className="clipped-frame overflow-hidden">
          <img src={IMAGES.night} alt="Refinery at night" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
        </div>
        <div className="absolute -bottom-6 -right-2 sm:-right-6 border border-amber-500/40 bg-white px-6 py-4">
          <p className="font-display text-4xl font-bold text-amber-500"><Counter to={24} suffix="h" testId="snapshot-years" /></p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Response pledge</p>
        </div>
      </Reveal>
      <div>
        <Reveal><Eyebrow>Who we are</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
            A trading desk built for industrial buyers
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 text-base leading-relaxed text-slate-700">
            Terra Bloom DWC LLC is a Dubai-based oil and petrochemicals trading house. We move rubber process oils,
            base oils, waxes and finished lubricants from Gulf and Asian refineries to manufacturers, blenders and
            distributors across the Middle East, Africa, Europe and Asia-Pacific.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            No retail noise, no spot-market gambling — just verified product, honest specs and freight that arrives
            when we say it will.
          </p>
        </Reveal>
        <Reveal delay={0.26}>
          <Link
            to="/about"
            data-testid="about-snapshot-link"
            className="mt-8 inline-flex items-center gap-2 border border-black/20 px-6 py-3 text-sm font-semibold text-[#0E1526] transition-colors hover:border-amber-500 hover:text-amber-600"
          >
            Our story <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const ProductsGrid = () => (
  <section data-testid="products-grid" className="border-y border-black/10 bg-[#F5F5F2] py-24 md:py-32 noise-surface">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Reveal><Eyebrow>Product range</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-xl font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
              Nine products. Zero compromise on spec.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <a
            href={SITE.brochure}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-brochure-button"
            onClick={() => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brochure-download`, { method: "POST" }).catch(() => {})}
            className="inline-flex items-center gap-2 border border-black/20 px-6 py-3 text-sm font-semibold text-[#0E1526] transition-colors hover:border-amber-500 hover:text-amber-600"
          >
            <Download className="h-4 w-4" /> Company Brochure
          </a>
        </Reveal>
      </div>
      {CATEGORIES.map((cat, ci) => (
        <div key={cat.id} className={ci > 0 ? "mt-16" : "mt-14"}>
          <Reveal>
            <div className="mb-8 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-mono text-xs text-amber-600">0{ci + 1}</span>
              <h3 className="font-display text-2xl font-semibold uppercase tracking-wide text-[#0E1526]">{cat.label}</h3>
              <span className="text-xs text-slate-500">{cat.blurb}</span>
            </div>
          </Reveal>
          <div className="grid gap-px border border-black/10 bg-black/[0.08] sm:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.filter((p) => p.category === cat.id).map((p) => {
              const i = PRODUCTS.indexOf(p);
              return (
            <Reveal key={p.slug} delay={(i % 3) * 0.1}>
            <Link
              to={`/products/${p.slug}`}
              data-testid={`product-tile-${p.slug}`}
              className="group relative block h-full overflow-hidden bg-white p-8 transition-colors hover:bg-[#F1F5F9]"
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500/80">{p.code}</span>
              </div>
              <h3 className="mt-10 font-display text-2xl font-semibold uppercase tracking-wide text-[#0E1526] transition-colors group-hover:text-amber-600">
                {p.shortName}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 line-clamp-3">{p.tagline}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 transition-colors group-hover:text-amber-600">
                View specs <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </Reveal>
              );
            })}
          </div>
        </div>
      ))}
      <div className="mt-16">
        <Reveal>
          <div className="mb-8 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-mono text-xs text-amber-600">03</span>
            <h3 className="font-display text-2xl font-semibold uppercase tracking-wide text-[#0E1526]">Steel & TMT Products</h3>
            <span className="text-xs text-slate-500">Line pipe, structural steel, TMT bars and piling pipes.</span>
          </div>
        </Reveal>
        <div className="grid gap-px border border-black/10 bg-black/[0.08] sm:grid-cols-2 lg:grid-cols-3">
          <Reveal>
            <Link
              to="/steel"
              data-testid="product-tile-steel"
              className="group relative block h-full overflow-hidden bg-white p-8 transition-colors hover:bg-[#F1F5F9]"
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-xs text-slate-600">10</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500/80">STL</span>
              </div>
              <h3 className="mt-10 font-display text-2xl font-semibold uppercase tracking-wide text-[#0E1526] transition-colors group-hover:text-amber-600">
                Steel & TMT Products
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                API 5L line pipe, structural steels, TMT bars and piling pipes for energy, water and construction
                projects — with mill test certificates on every lot.
              </p>
              <span className="mt-8 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 transition-colors group-hover:text-amber-600">
                View steel range <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Doctrine = () => (
  <section data-testid="doctrine-section" className="bg-white py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal><Eyebrow>Our doctrine</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
          Why procurement teams stay with us
        </h2>
      </Reveal>
      <div className="mt-16 space-y-0">
        {DOCTRINE.map((d, i) => (
          <Reveal key={d.n} delay={i * 0.08}>
            <div className="group grid gap-4 border-t border-black/10 py-10 transition-colors last:border-b hover:bg-[#F8FAFC] md:grid-cols-[120px_1fr_2fr] md:items-baseline md:px-6">
              <span className="font-mono text-sm text-amber-500">{d.n}.</span>
              <h3 className="font-display text-3xl font-semibold uppercase tracking-wide text-[#0E1526]">{d.title}</h3>
              <p className="max-w-xl text-sm leading-relaxed text-slate-600">{d.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const StatsBand = () => (
  <section data-testid="stats-band" className="relative overflow-hidden border-y border-white/10">
    <img src={IMAGES.sunset} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-20" />
    <div className="absolute inset-0 bg-[#0A101D]/70" />
    <div className="relative mx-auto grid max-w-7xl grid-cols-2 gap-y-12 px-5 py-20 md:grid-cols-4 lg:px-8">
      {STATS.map((s, i) => (
        <div key={s.label} className="text-center" data-testid={`stat-${i}`}>
          <p className="font-display text-5xl font-bold text-white sm:text-6xl">
            <Counter to={s.value} suffix={s.suffix} decimals={s.decimals} testId={`stat-value-${i}`} />
          </p>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">{s.label}</p>
        </div>
      ))}
    </div>
  </section>
);

const CtaBand = () => (
  <section data-testid="contact-cta-band" className="relative overflow-hidden noise-surface">
    <img src={IMAGES.tanker} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-[#0A101D]/85" />
    <div className="relative mx-auto max-w-7xl px-5 py-24 md:py-32 lg:px-8">
      <Reveal><Eyebrow>Start a conversation</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-bold uppercase leading-tight text-white sm:text-6xl">
          Need a quote on spec, quantity & port?
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">
          Send us your grade, volume and destination. Our trading desk replies within one business day with
          availability, pricing and freight options.
        </p>
      </Reveal>
      <Reveal delay={0.3} className="mt-10 flex flex-wrap gap-4">
        <Link
          to="/contact"
          data-testid="cta-band-quote-button"
          className="group inline-flex items-center gap-2 bg-amber-500 px-8 py-4 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
        >
          Request a Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
        <a
          href={SITE.whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cta-band-whatsapp-button"
          className="inline-flex items-center gap-2 border border-emerald-500/60 px-8 py-4 text-sm font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/10"
        >
          WhatsApp the desk
        </a>
      </Reveal>
    </div>
  </section>
);

const SteelTeaser = () => (
  <section data-testid="steel-teaser" className="relative isolate overflow-hidden noise-surface">
    <img src={IMAGES.steelPlant} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-[#0A101D]/85" />
    <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-5 py-20 lg:px-8">
      <div className="max-w-2xl">
        <Reveal><Eyebrow>Now trading</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-tight text-white sm:text-5xl">
            Steel products
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-4 text-base leading-relaxed text-slate-300">
            API 5L line pipe, structural steels, TMT bars and piling pipes — for energy, water and construction
            projects, with mill test certificates on every lot.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.25}>
        <Link
          to="/steel"
          data-testid="steel-teaser-button"
          className="group inline-flex items-center gap-2 bg-amber-500 px-8 py-4 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
        >
          Explore Steel <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <AttributesStrip />
      <Marquee items={["Rubber Process Oil", "Base Oils SN150 — SN500", "Semi-Refined Paraffin Wax", "Engine Oils API SN", "Slack Wax", "Residue Wax", "Polybutadiene PBR", "SBR 1502 / 1712", "Styrenated Phenol", "FOB — CFR — CIF — DAP"]} />
      <AboutSnapshot />
      <ProductsGrid />
      <SteelTeaser />
      <Doctrine />
      <StatsBand />
      <CtaBand />
    </>
  );
}
